/* buildRfpPayload — pure payload constructor for the
   POST /api/brand/waves/generate-rfps request body.

   Lives in its own module so the wire-layer seam between BriefAuthor
   (which authors a structured object) and the backend API (which
   requires the SSP-canonical structured object) can be unit-tested
   in isolation. This is the function that caused the iter-70 P0 UI
   bug — it used to call `.trim()` on `brief.format_spec` after that
   field migrated from string to object.

   Cross-plane invariant: payload.brief must produce the SSP-canonical
   v1 shape (fingerprint f424908b…1af9e27d) byte-for-byte:
     {
       goal: <one of CANONICAL_GOALS>,
       format_spec: {
         aspect_ratio: <one of CANONICAL_ASPECT_RATIOS>,
         duration_seconds_max: int,
         hook_first_seconds: int,
         voiceover_required: bool,
         b_roll_required: bool,
       },
       message_points: [str],   // 1..3
       creative_freedom: str,
     }
*/

export function buildBriefPayload(brief) {
  const trimmedPoints = (brief?.message_points || [])
    .map(p => (p || '').trim())
    .filter(Boolean);
  const fs = brief?.format_spec || {};
  return {
    goal: brief?.goal || '',
    format_spec: {
      aspect_ratio: fs.aspect_ratio,
      duration_seconds_max: Number(fs.duration_seconds_max),
      hook_first_seconds: Number(fs.hook_first_seconds),
      voiceover_required: !!fs.voiceover_required,
      b_roll_required: !!fs.b_roll_required,
    },
    message_points: trimmedPoints,
    creative_freedom: (brief?.creative_freedom || '').trim(),
  };
}

export function buildRfpPayload({
  campaignGoal,
  selectedCreatorList,
  budget,
  platforms,
  brief,
  briefClonedFrom,
}) {
  return {
    goal: campaignGoal,
    creators: (selectedCreatorList || []).map((c) => ({
      name: c.name,
      handle: c.handle,
      velocity: typeof c.velocity === 'number'
        ? `${(c.velocity / 10).toFixed(1)}x`
        : c.velocity,
      platform: c.platform || 'Instagram',
    })),
    budget: Number(budget),
    platforms,
    wave_window: '±5 min',
    brief: buildBriefPayload(brief),
    // Cloned-from provenance — recorded on the wave doc so an auditor
    // can confirm which wave the brief mirrors (ladder integrity).
    brief_cloned_from: briefClonedFrom || null,
  };
}
